import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Pill } from "../hs/hsUtils";
import { TONE_CLASSES } from "../assets/assetUtils";
import { Stars, areaText, availText, fmt, gbp } from "./mpUtils";
import { AWAY_TONE, awayText } from "./ListingCard";
import { Check, X } from "@phosphor-icons/react";

const yes = <Check size={16} className="text-emerald-600 inline" />;
const no = <X size={16} className="text-muted-foreground inline" />;

function insurance(c) {
  const pl = (c.documents || []).find((d) => d.kind === "public_liability");
  if (!pl) return <span className="text-muted-foreground">None on file</span>;
  return <span className={pl.expired ? "text-rose-600" : ""}>{pl.expired ? "Expired" : "Valid"}{pl.expiry ? ` to ${fmt(pl.expiry)}` : ""}{pl.verified ? " · verified" : ""}</span>;
}

const ROWS = [
  ["Rating", (c) => <Stars value={c.rating_avg} count={c.rating_count} size={13} />],
  ["Jobs via Fixlr", (c) => c.jobs_completed || 0],
  ["Reply time", (c) => c.response_label || <span className="text-muted-foreground">Not enough data</span>],
  ["Availability", (c) => <span data-testid="cmp-away">{awayText(c.away) ? <Pill tone={AWAY_TONE}>{awayText(c.away)}</Pill> : <Pill tone={TONE_CLASSES.ok}>Available</Pill>}</span>],
  ["Distance", (c) => (c.distance?.miles != null ? `${c.distance.miles} mi ${c.distance.kind === "driving" ? "drive" : ""}` : <span className="text-muted-foreground">—</span>)],
  ["Trades", (c) => <div className="flex flex-wrap gap-1">{c.trades.map((t) => <Pill key={t} tone={TONE_CLASSES.none}>{t}</Pill>)}{(c.custom_trades || []).map((t) => <span key={t} className="rounded-full border border-dashed border-border px-2 py-0.5 text-[11px] text-muted-foreground">{t}</span>)}</div>],
  ["Coverage", (c) => areaText(c.areas)],
  ["Days & hours", (c) => availText(c.availability)],
  ["24/7 call-outs", (c) => (c.availability?.emergency_callouts ? yes : no)],
  ["Hourly rate", (c) => gbp(c.rates?.hourly)],
  ["Call-out", (c) => gbp(c.rates?.callout)],
  ["Day rate", (c) => gbp(c.rates?.day)],
  ["VAT registered", (c) => (c.rates?.vat_registered ? yes : no)],
  ["Public liability", insurance],
  ["Other accreditations", (c) => { const o = (c.documents || []).filter((d) => d.kind !== "public_liability"); return o.length ? o.map((d) => <div key={d.id} className={d.expired ? "text-rose-600" : ""}>{d.label}{d.expiry ? ` · ${fmt(d.expiry)}` : ""}</div>) : <span className="text-muted-foreground">—</span>; }],
  ["Team size", (c) => c.team_size || <span className="text-muted-foreground">—</span>],
  ["Listed since", (c) => fmt(c.member_since)],
];

export function CompareDialog({ contractors, open, onClose, onRemove }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl sm:max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="compare-dialog">
        <DialogHeader><DialogTitle className="font-[Chivo]">Compare contractors</DialogTitle></DialogHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="text-left p-2 w-40 align-bottom text-xs text-muted-foreground font-medium">Side by side</th>
                {contractors.map((c) => (
                  <th key={c.id} className="text-left p-2 align-bottom border-b border-border" data-testid={`cmp-col-${c.id}`}>
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1"><p className="font-semibold leading-tight">{c.company_name}</p><p className="text-[11px] text-muted-foreground truncate">{c.contact?.name}</p></div>
                      <button onClick={() => onRemove(c)} className="text-muted-foreground hover:text-foreground" title="Remove" data-testid={`cmp-remove-${c.id}`}><X size={14} /></button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([label, render]) => (
                <tr key={label} className="odd:bg-muted/30">
                  <td className="p-2 text-xs font-medium text-muted-foreground align-top">{label}</td>
                  {contractors.map((c) => <td key={c.id} className="p-2 align-top" data-testid={`cmp-${label.replace(/\W+/g, "-").toLowerCase()}-${c.id}`}>{render(c)}</td>)}
                </tr>
              ))}
              <tr>
                <td className="p-2" />
                {contractors.map((c) => <td key={c.id} className="p-2"><Button asChild size="sm" className="w-full"><Link to={`/marketplace/contractors/${c.id}`} data-testid={`cmp-view-${c.id}`}>View & request quote</Link></Button></td>)}
              </tr>
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
